import { Button, Space, Spin, Tabs, Typography } from 'antd';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import IRfiItem from '~Api/Application/IRfiItem';
import {
    applicationBorrowersListAction,
    applicationConditionDocumentsListAction,
    applicationConditionsListAction,
    applicationGetAction,
    applicationRfiItemsListAction,
} from '~Applications/actions';
import {
    applicationBorrowersSelector,
    applicationConditionDocumentsByConditionUuidSelector,
    applicationEntityConditionsSelector,
    applicationPropertiesSelector,
    applicationRfiItemsSelector,
    applicationSelector,
} from '~Applications/selectors';
import ConditionDocumentsList from '~Applications/Application/ConditionDocumentsList';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import { aggregatorsSelector } from '~Aggregators/selectors';
import { aggregatorsListAction } from '~Aggregators/actions';
import IAggregator from '~Api/Aggregator/IAggregator';
import ConditionAddModal from './ConditionAddModal';
import ModalStateEnum from '~UI/ModalStateEnum';
import HashTabs from '~UI/HashTabs';
import SatisfiedStatusEnum from '~Api/Application/SatisfiedStatusEnum';
import { ImCheckmark, ImCross } from 'react-icons/im';

const nextWorkflowStatuses: IDictionary<WorkflowStatusEnum> = {
    [WorkflowStatusEnum.Draft]: WorkflowStatusEnum.New,
    [WorkflowStatusEnum.New]: WorkflowStatusEnum.ConditionalApproval,
    [WorkflowStatusEnum.ConditionalApproval]: WorkflowStatusEnum.Underwriting,
    [WorkflowStatusEnum.Underwriting]: WorkflowStatusEnum.FormalApproval,
    [WorkflowStatusEnum.FormalApproval]: WorkflowStatusEnum.LegalDocuments,
    [WorkflowStatusEnum.LegalDocuments]: WorkflowStatusEnum.Settlement,
    [WorkflowStatusEnum.Settlement]: WorkflowStatusEnum.Warehoused,
};

export default function Underwriting(): ReactElement {
    const { applicationUuid } = useParams<{ applicationUuid: string }>();
    const [ isAddConditionModalOpen, setIsAddConditionModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ openModalEntityUuid, setOpenModalEntityUuid ] = useState<string>(null);

    const aggregators: IDictionary<IAggregator> = useSelector(aggregatorsSelector);
    const application: IApplication = useSelector((state: IGlobalState): IApplication => applicationSelector(state, applicationUuid));
    const applicationEntityConditions: IDictionary<IDictionary<ICondition>> = useSelector((state: IGlobalState): IDictionary<IDictionary<ICondition>> => applicationEntityConditionsSelector(state, applicationUuid));
    const applicationProperties: IDictionary<IApplicationProperty> = useSelector((state: IGlobalState): IDictionary<IApplicationProperty> => applicationPropertiesSelector(state, applicationUuid));
    const borrowers: IDictionary<IBorrower> = useSelector((state: IGlobalState): IDictionary<IBorrower> => applicationBorrowersSelector(state, applicationUuid));
    const conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]> = useSelector((state: IGlobalState): IDictionary<IConditionDocument[]> => applicationConditionDocumentsByConditionUuidSelector(state, applicationUuid));
    const rfiItems: IDictionary<IRfiItem> = useSelector((state: IGlobalState): IDictionary<IRfiItem> => applicationRfiItemsSelector(state, applicationUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!aggregators) {
            dispatch(aggregatorsListAction());
        }
    }, [
        aggregators,
        dispatch,
    ]);

    useEffect((): void => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        application,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!borrowers) {
            dispatch(applicationBorrowersListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        borrowers,
        dispatch,
    ]);

    useEffect((): void => {
        if (!conditionDocumentsByConditionUuid) {
            dispatch(applicationConditionDocumentsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        conditionDocumentsByConditionUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!applicationEntityConditions) {
            dispatch(applicationConditionsListAction(applicationUuid));
        }
    }, [
        applicationEntityConditions,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!rfiItems) {
            dispatch(applicationRfiItemsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        dispatch,
        rfiItems,
    ]);

    useEffect((): () => void => {
        const refreshInterval: NodeJS.Timeout = setInterval((): void => {
            dispatch(applicationConditionDocumentsListAction(applicationUuid));
            dispatch(applicationConditionsListAction(applicationUuid));
            dispatch(applicationRfiItemsListAction(applicationUuid));
        }, 5 * 60 * 1000);

        return (): void => clearInterval(refreshInterval);
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickAddConditionModal: (openModalEntityUuid: string) => void = useCallback((openModalEntityUuid: string): void => {
        setIsAddConditionModalOpen(ModalStateEnum.Open);
        setOpenModalEntityUuid(openModalEntityUuid);
    }, []);

    const onCancelAddConditionModal: () => void = useCallback((): void => {
        setIsAddConditionModalOpen(ModalStateEnum.Closed);
        setOpenModalEntityUuid(null);
    }, []);

    const entityConditionDocumentsComplete: (entityUuid: string) => boolean = useCallback((entityUuid: string): boolean => {
        return _.every(applicationEntityConditions[entityUuid] || [], (condition: ICondition): boolean => (
            !condition.requiredWorkflowStatuses.includes(nextWorkflowStatuses[application.workflowStatus])
            || condition.satisfiedStatus === SatisfiedStatusEnum.Satisfied
        ));
    }, [
        application,
        applicationEntityConditions,
    ]);

    if (
        !aggregators ||
        !application ||
        !applicationEntityConditions ||
        !applicationProperties ||
        !borrowers ||
        !conditionDocumentsByConditionUuid ||
        !rfiItems
    ) {
        return (
            <Layout applicationUuid={applicationUuid} section='underwriting'>
                <Typography.Title level={2}>Underwriting Checklist</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const borrowerTabsBlock: JSX.Element[] = _.map(borrowers, (borrower: IBorrower): JSX.Element => {
        const onClickAddApplicationBorrowerCondition: () => void = (): void => onClickAddConditionModal(borrower.uuid);
        const borrowerConditionModal: ReactElement = openModalEntityUuid === borrower.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
            <ConditionAddModal
                application={application}
                applicationBorrower={borrower}
                isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                onCancel={onCancelAddConditionModal}
            />
        );

        const tabTitle: ReactElement = (
            <Space>
                {entityConditionDocumentsComplete(borrower.uuid) ? <ImCheckmark className='satisfied-status satisfied' /> : <ImCross className='satisfied-status not-satisfied' />}
                <Typography.Text>{borrower.dealBorrower.formattedName}</Typography.Text>
            </Space>
        );

        return (
            <Tabs.TabPane
                tab={tabTitle}
                key={`borrower-${borrower.uuid}`}
            >
                <ConditionDocumentsList
                    key={`condition-document-borrower-${borrower.uuid}`}
                    application={application}
                    applicationBorrower={borrower}
                />
                <Button className='add-condition' onClick={onClickAddApplicationBorrowerCondition}>Add Condition</Button>
                {borrowerConditionModal}
            </Tabs.TabPane>
        );
    });

    const propertyTabsBlock: JSX.Element[] = _.reduce(applicationProperties, (tabs: JSX.Element[], applicationProperty: IApplicationProperty): JSX.Element[] => {
        if (applicationProperty.dealProperty.dischargeTime) {
            return tabs;
        }

        const onClickAddApplicationPropertyCondition: () => void = (): void => onClickAddConditionModal(applicationProperty.uuid);
        const propertyConditionModal: ReactElement = openModalEntityUuid === applicationProperty.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
            <ConditionAddModal
                application={application}
                applicationProperty={applicationProperty}
                isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                onCancel={onCancelAddConditionModal}
            />
        );
        const tabTitle: ReactElement = (
            <Space>
                {entityConditionDocumentsComplete(applicationProperty.uuid) ? <ImCheckmark className='satisfied-status satisfied' /> : <ImCross className='satisfied-status not-satisfied' />}
                <Typography.Text>{applicationProperty.dealProperty.streetAddress}</Typography.Text>
            </Space>
        );

        tabs.push(
            <Tabs.TabPane
                tab={tabTitle}
                key={`property-${applicationProperty.uuid}`}
            >
                <ConditionDocumentsList
                    key={`condition-document-property${applicationProperty.uuid}`}
                    application={application}
                    applicationProperty={applicationProperty}
                />
                <Button className='add-condition' onClick={onClickAddApplicationPropertyCondition}>Add Condition</Button>
                {propertyConditionModal}
            </Tabs.TabPane>,
        );

        return tabs;
    }, []);

    const solictorDownloadsButton: JSX.Element = !application.isExtension && (
        <Link to={`/applications/${application.uuid}/solicitor-downloads`}>
            <Button>Download Documents</Button>
        </Link>
    );
    const onClickAddApplicationCondition: () => void = (): void => onClickAddConditionModal(application.uuid);
    const applicationConditionModal: ReactElement = openModalEntityUuid === application.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
        <ConditionAddModal
            application={application}
            isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
            onCancel={onCancelAddConditionModal}
        />
    );

    const tabTitle: ReactElement = (
        <Space>
            {entityConditionDocumentsComplete(application.uuid) ? <ImCheckmark className='satisfied-status satisfied' /> : <ImCross className='satisfied-status not-satisfied' />}
            <Typography.Text>Application</Typography.Text>
        </Space>
    );

    return (
        <Layout applicationUuid={applicationUuid} section='underwriting'>
            <Space className='actions'>
                {solictorDownloadsButton}
            </Space>
            <Typography.Title level={2}>Underwriting Checklist</Typography.Title>
            <HashTabs defaultActiveKey={`application-${application.uuid}`} type='card'>
                <Tabs.TabPane
                    tab={tabTitle}
                    key={`application-${application.uuid}`}
                >
                    <ConditionDocumentsList
                        key={`condition-document-application-${application.uuid}`}
                        application={application}
                    />
                    <Button className='add-condition' onClick={onClickAddApplicationCondition}>Add Condition</Button>
                    {applicationConditionModal}
                </Tabs.TabPane>
                {borrowerTabsBlock}
                {propertyTabsBlock}
            </HashTabs>
        </Layout>
    );
}
